import { useState } from 'react';
import { cn } from '@/lib/cn';

interface StarRatingProps {
  value: number;            // 0..5 (can be fractional for display)
  count?: number;           // Total stars (default 5)
  onChange?: (next: number) => void;   // Provide → interactive
  size?: number;            // Pixel size of each star
  className?: string;
  label?: string;           // a11y override; default "Rated X out of 5"
}

export function StarRating({
  value,
  count = 5,
  onChange,
  size = 16,
  className,
  label,
}: StarRatingProps) {
  const [hover, setHover] = useState<number | null>(null);
  const interactive = !!onChange;
  const display = hover ?? value;

  const ariaLabel = label ?? `Rated ${value.toFixed(1)} out of ${count}`;

  return (
    <div
      role={interactive ? 'radiogroup' : 'img'}
      aria-label={ariaLabel}
      className={cn('inline-flex items-center gap-0.5', className)}
      onMouseLeave={() => setHover(null)}
    >
      {Array.from({ length: count }, (_, i) => {
        const starValue = i + 1;
        const fillPct = Math.max(0, Math.min(1, display - i));

        // SVG attributes (e.g. `<stop offset>`) cannot reference CSS variables —
        // they must be a literal number or percentage. Compute the percentage
        // directly so the gradient renders.
        const fillOffset = `${(fillPct * 100).toFixed(2)}%`;
        const star = (
          <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            aria-hidden
          >
            <defs>
              <linearGradient id={`star-${i}-${value}`} x1="0" y1="0" x2="100%" y2="0">
                <stop offset={fillOffset} stopColor="var(--color-gold-light)" />
                <stop offset={fillOffset} stopColor="rgba(255,255,255,0.18)" />
              </linearGradient>
            </defs>
            <path
              d="M12 2.5l2.95 6.32 6.55.7-4.94 4.5 1.45 6.45L12 17.27 5.99 20.47l1.45-6.45-4.94-4.5 6.55-.7L12 2.5z"
              fill={`url(#star-${i}-${value})`}
              stroke="rgba(255,255,255,0.25)"
              strokeWidth="0.6"
            />
          </svg>
        );

        return interactive ? (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={value === starValue}
            aria-label={`${starValue} star${starValue === 1 ? '' : 's'}`}
            className="cursor-pointer rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand-magenta)]"
            onMouseEnter={() => setHover(starValue)}
            onClick={() => onChange?.(starValue)}
          >
            {star}
          </button>
        ) : (
          <span key={i} className="inline-block">
            {star}
          </span>
        );
      })}
    </div>
  );
}
